import { useCallback, useEffect, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, Loader2, MoveRight } from "lucide-react";
import { api } from "../lib/api";
import { fmtAxv, fmtAxvTrim, IMAGES, SUB } from "../lib/tokens";
import { useWorld } from "../state/World";
import Plate from "../components/Plate";
import FeeLines, { Receipt } from "../components/FeeLines";

const FALLBACK_OPTIONS = [1, 3, 5, 10];

export default function PlayState() {
  const { me, refresh, mark, cross } = useWorld();
  const nav = useNavigate();
  const [card, setCard] = useState(null);
  const [cadences, setCadences] = useState([]);
  const [axv, setAxv] = useState(5);
  const [quote, setQuote] = useState(null);
  const [tx, setTx] = useState(null);
  const [busy, setBusy] = useState(false);
  const [crossing, setCrossing] = useState(false);

  useEffect(() => {
    mark("playforward", "/playforward/play");
    api
      .get("/playforward/playcard")
      .then(({ data }) => setCard(data))
      .catch(() => nav("/playforward/cove"));
    api
      .get("/playforward/cadences")
      .then(({ data }) => setCadences(data.cadences || []))
      .catch(() => setCadences([]));
  }, [mark, nav]);

  // The Ante band scales with the declared window — you cannot stake a year's
  // worth on an afternoon.
  const cadence = cadences.find((c) => c.key === card?.cadence);
  const options = cadence?.ante_axv || FALLBACK_OPTIONS;

  useEffect(() => {
    if (options.length && !options.includes(axv)) setAxv(options[0]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cadence?.key]);

  const loadQuote = useCallback(async (amount) => {
    const { data } = await api.get("/playforward/quote", {
      params: { amount_sub: amount * SUB },
    });
    setQuote(data);
  }, []);

  useEffect(() => {
    loadQuote(axv);
  }, [axv, loadQuote]);

  const ante = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/playforward/ante", {
        playcard_id: card.id,
        amount_sub: axv * SUB,
      });
      setTx(data.transaction);
      setCard((c) => ({ ...c, ante_sub: axv * SUB, state: "in_play" }));
      await refresh();
      toast.success("Ante posted to the Ledger. You are in Play State.");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "The Ledger refused that move.");
    } finally {
      setBusy(false);
    }
  };

  // The Seam, in both halves: the substrate makes the Project from the PlayCard,
  // then identity crosses the origin boundary into Stageward. Works whether the
  // user was born at the Atlas or walked in through the PlayForward front door.
  const crossSeam = async () => {
    setCrossing(true);
    try {
      await api.post("/seam/open-stageward", {});
      const { external } = await cross("stageward", "/stageward/walk");
      if (external) return;
      setTimeout(() => nav("/stageward/walk"), 1400);
    } catch (e) {
      setCrossing(false);
      toast.error(e?.response?.data?.detail || "The Seam did not open.");
    }
  };

  if (!card) {
    return (
      <div className="flex min-h-screen items-center justify-center">
        <Loader2 className="animate-spin text-white/30" />
      </div>
    );
  }

  const anted = card.state === "in_play";

  return (
    <div className="relative min-h-screen overflow-hidden">
      <Plate src={IMAGES.pfLounge} depth={10} />

      <AnimatePresence>
        {crossing && (
          <motion.div
            className="fixed inset-0 z-50 flex items-center justify-center"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            data-testid="seam-transition"
          >
            <motion.div
              className="absolute inset-0 bg-[#040302]"
              initial={{ opacity: 0 }}
              animate={{ opacity: 0.985 }}
              transition={{ duration: 0.85, ease: "easeIn" }}
            />
            <motion.div
              className="absolute inset-0"
              initial={{ opacity: 0 }}
              animate={{ opacity: [0, 0.5, 0] }}
              transition={{ duration: 1.5, times: [0, 0.25, 1] }}
              style={{
                background:
                  "radial-gradient(ellipse at center, rgba(47,227,240,0.28), transparent 62%)",
              }}
            />
            <motion.div
              className="relative text-center"
              initial={{ opacity: 0, scale: 0.94 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.8, delay: 0.2 }}
            >
              <motion.div
                className="mx-auto mb-8 h-px"
                initial={{ width: 0 }}
                animate={{ width: 320 }}
                transition={{ duration: 1.1 }}
                style={{
                  background:
                    "linear-gradient(to right, transparent, var(--arcade-cyan), var(--stage-gold), transparent)",
                }}
              />
              <div className="tracked mb-4 text-[10px] text-white/40">The Seam</div>
              <div className="serif text-2xl font-light italic text-[#f2e7d3] sm:text-3xl">
                Same you. Same Pouch. Same MelzAI.
              </div>
              <div className="mono mt-5 text-[10px] text-white/35">
                carrying {fmtAxvTrim(me?.pouch_sub || 0)} Tokens into Stageward
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      <div className="relative z-10 mx-auto max-w-6xl px-6 pb-28 pt-20 md:px-10">
        <button
          type="button"
          data-testid="back-to-cove"
          onClick={() => nav("/playforward/cove")}
          className="tracked mb-12 flex items-center gap-2 text-[9px] text-white/40 transition-colors duration-200 hover:text-[var(--arcade-cyan)]"
        >
          <ArrowLeft size={12} /> The Cove
        </button>

        <div className="grid gap-8 lg:grid-cols-[1.25fr_1fr]">
          {/* The PlayCard */}
          <motion.div
            initial={{ opacity: 0, rotateX: -8, y: 30 }}
            animate={{ opacity: 1, rotateX: 0, y: 0 }}
            transition={{ duration: 1.1, ease: [0.2, 0.8, 0.2, 1] }}
            className="glass-warm relative overflow-hidden rounded-2xl p-8 md:p-10"
            data-testid="playcard"
          >
            <div
              className="absolute right-0 top-0 h-40 w-40 opacity-30"
              aria-hidden
              style={{
                background:
                  "radial-gradient(circle at top right, var(--stage-gold), transparent 70%)",
              }}
            />
            <div className="relative">
              <div className="tracked mb-6 flex items-center justify-between text-[9px]">
                <span className="text-[var(--stage-gold)]/75">PlayCard</span>
                <span className="text-white/30" data-testid="playcard-state">
                  {anted ? "Play State" : "Unanted"}
                </span>
              </div>

              <div className="mb-2 flex items-baseline gap-2">
                <span className="mono shrink-0 text-[8px] uppercase text-white/30">Horizon</span>
                <span
                  className="text-[12.5px] leading-snug text-white/45"
                  data-testid="playcard-aim"
                >
                  {card.aim}
                </span>
              </div>

              <div className="mono mb-2 text-[8px] uppercase text-[var(--stage-gold)]/70">
                The Slice
              </div>
              <h1
                className="serif text-3xl font-light leading-tight text-[#f7ecd8] sm:text-4xl"
                data-testid="playcard-slice"
              >
                {card.slice || card.what || card.aim}
              </h1>

              <div className="mb-9 mt-5 flex flex-wrap items-center gap-x-5 gap-y-2 border-t border-white/8 pt-4">
                <div className="flex items-baseline gap-2">
                  <span className="mono text-[8px] uppercase text-white/30">Cadence</span>
                  <span
                    className="mono text-[12px] text-[var(--arcade-cyan)]"
                    data-testid="playcard-cadence"
                  >
                    {card.cadence_name || "—"}
                  </span>
                </div>
                <div className="flex items-baseline gap-2">
                  <span className="mono text-[8px] uppercase text-white/30">Deadline</span>
                  <span
                    className="mono text-[12px] text-[#f7e6c8]"
                    data-testid="playcard-deadline"
                  >
                    {card.deadline ? new Date(card.deadline).toLocaleDateString() : "—"}
                  </span>
                </div>
              </div>

              <div className="space-y-6">
                {[
                  ["What", card.what, "That", card.that],
                  ["Where", card.where, "There", card.there],
                  ["When", card.when, "Then", card.then],
                ].map(([vl, vv, sl, sv]) => (
                  <div key={vl} className="grid gap-4 border-t border-white/8 pt-5 sm:grid-cols-2">
                    <div>
                      <div className="mono mb-1.5 text-[9px] text-white/30">{vl}</div>
                      <div className="text-[13px] leading-snug text-white/45">{vv || "—"}</div>
                    </div>
                    <div>
                      <div className="mono mb-1.5 text-[9px] text-[var(--stage-gold)]/70">{sl}</div>
                      <div className="serif text-[15px] leading-snug text-[#f6ead4]">
                        {sv || "—"}
                      </div>
                    </div>
                  </div>
                ))}
              </div>

              {anted && (
                <div className="mono mt-9 border-t border-white/8 pt-5 text-[11px] text-white/45">
                  Self-ante held:{" "}
                  <span className="text-[var(--arcade-cyan)]" data-testid="playcard-ante">
                    {fmtAxv(card.ante_sub)} AXV
                  </span>{" "}
                  · no leaderboard, no opponent
                </div>
              )}
            </div>
          </motion.div>

          {/* Ante / Seam */}
          <div className="space-y-5">
            {!anted ? (
              <motion.div
                initial={{ opacity: 0, y: 22 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.9, delay: 0.25 }}
                className="glass rounded-2xl p-7"
                data-testid="ante-panel"
              >
                <div className="tracked mb-3 text-[9px] text-white/40">The self-ante</div>
                <p className="mb-6 text-sm leading-relaxed text-white/50">
                  Put Tokens on the Slice, not the horizon. They move out of your Pouch
                  into your own stake — you are not paying us for the aim, you are making
                  it cost something.
                  {cadence && (
                    <span className="mt-2 block text-white/35">
                      A {cadence.name} carries a {cadence.window} window, so the band is{" "}
                      {options.join(" · ")} Tokens.
                    </span>
                  )}
                </p>
                <div className="mb-6 flex gap-2">
                  {options.map((o) => (
                    <button
                      key={o}
                      type="button"
                      data-testid={`ante-option-${o}`}
                      onClick={() => setAxv(o)}
                      className={`mono flex-1 rounded-lg border py-3 text-[13px] transition-colors duration-200 ${
                        axv === o
                          ? "border-[var(--stage-gold)]/60 bg-[var(--stage-gold)]/10 text-[var(--stage-gold)]"
                          : "border-white/12 text-white/40 hover:border-white/25"
                      }`}
                    >
                      {o}
                    </button>
                  ))}
                </div>
                {quote && (
                  <div className="mb-6 rounded-xl border border-white/8 bg-black/30 p-4">
                    <FeeLines
                      principalLabel="Ante into your stake"
                      principalSub={quote.principal_sub}
                      feeLines={quote.fee_lines}
                      totalSub={quote.total_sub}
                      testid="ante-fee-lines"
                    />
                  </div>
                )}
                <button
                  type="button"
                  data-testid="ante-confirm-button"
                  disabled={busy}
                  onClick={ante}
                  className="btn btn-gold w-full"
                >
                  {busy ? (
                    <Loader2 className="mx-auto animate-spin" size={14} />
                  ) : (
                    `Ante ${axv} Tokens on yourself`
                  )}
                </button>
                <p className="mono mt-4 text-[9px] leading-relaxed text-white/25">
                  Fees are flat integers, never a percentage. Every sub-unit is
                  written to an append-only Ledger.
                </p>
              </motion.div>
            ) : (
              <>
                <Receipt tx={tx} />
                <motion.div
                  initial={{ opacity: 0, y: 22 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.9, delay: 0.2 }}
                  className="glass rounded-2xl p-7"
                  data-testid="seam-panel"
                >
                  <div className="tracked mb-3 text-[9px] text-[var(--arcade-cyan)]/70">
                    …but how?
                  </div>
                  <p className="mb-6 text-sm leading-relaxed text-white/50">
                    PlayForward holds the <em className="not-italic text-white/70">what</em>.
                    The <em className="not-italic text-white/70">how</em> lives backstage.
                    Cross the Seam and nothing about you resets — same identity, same
                    Pouch, same MelzAI, and this card comes with you.
                  </p>
                  <button
                    type="button"
                    data-testid="open-in-stageward-button"
                    onClick={crossSeam}
                    className="btn btn-cyan flex w-full items-center justify-center gap-3"
                  >
                    Open in Stageward <MoveRight size={14} />
                  </button>
                </motion.div>
              </>
            )}

            <button
              type="button"
              data-testid="view-ledger-button"
              onClick={() => nav("/ledger")}
              className="mono w-full text-left text-[10px] text-white/30 transition-colors duration-200 hover:text-[var(--stage-gold)]"
            >
              → inspect the Ledger
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
